import React from 'react'
import { Dialog, Heading, Modal, ModalOverlay } from 'react-aria-components'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

import { Button } from '#components/Button'
import { Checkbox } from '#components/Form'
import { PROFILE_CONSENT_LINKS } from '#pages/Profile/components/ProfileEditForm/constants'

import type { IConsentReconfirmFormData, IProps } from './interfaces'
import styles from './styles.module.scss'

/**
 * Blocking modal shown to legacy-consent users (decision B-12): they agreed to
 * the terms before the audit trail existed and must re-confirm before using the
 * app. Purely presentational — the parent gate owns the profile data, the save
 * mutation, and the log-out.
 *
 * Blocking mechanics: the underlying react-aria `ModalOverlay` is forced open
 * (`isOpen`) and cannot be dismissed — `isDismissable={false}` disables backdrop
 * clicks and `isKeyboardDismissDisabled` disables Escape. There is no close
 * button. The only ways out are confirming the terms or logging out.
 */
export const ConsentReconfirmModal = ({
  onConfirm,
  onLogout,
  isConfirming = false,
  isLoggingOut = false,
}: IProps): React.JSX.Element => {
  // 2. Hooks
  const { t } = useTranslation()
  const { control, watch } = useForm<IConsentReconfirmFormData>({
    defaultValues: { conditionsAgreed: false },
  })

  // 3. Derived state
  const hasAgreed = !!watch('conditionsAgreed')

  // 7. Render
  return (
    <ModalOverlay
      isOpen
      isDismissable={false}
      isKeyboardDismissDisabled
      className={styles.backdrop}
      data-testid="consent-reconfirm-backdrop"
    >
      <Modal className={styles.panel}>
        <Dialog
          className={styles.dialog}
          role="alertdialog"
          aria-label={t('Confirm the terms and conditions')}
          aria-describedby="consent-reconfirm-body"
        >
          <Heading slot="title" className={styles.title}>
            {t('Confirm the terms and conditions')}
          </Heading>

          <p id="consent-reconfirm-body" className={styles.body}>
            {t(
              'The way we record your agreement to the terms has changed. To keep using {{appName}}, please confirm once more that you agree to the privacy statement and terms of use.',
            )}
          </p>

          <Checkbox
            name="conditionsAgreed"
            control={control}
            data-testid="consent-reconfirm-checkbox"
            label={
              <Trans
                i18nKey="I agree to the <privacyLink>privacy statement</privacyLink> and <termsLink>terms of use</termsLink>."
                components={{
                  privacyLink: (
                    <a
                      href={PROFILE_CONSENT_LINKS.privacy}
                      target="_blank"
                      rel="noreferrer noopener"
                      className={styles.consentLink}
                      onPointerDown={(event): void => event.stopPropagation()}
                      onClick={(event): void => event.stopPropagation()}
                    >
                      {t('privacy statement')}
                    </a>
                  ),
                  termsLink: (
                    <a
                      href={PROFILE_CONSENT_LINKS.terms}
                      target="_blank"
                      rel="noreferrer noopener"
                      className={styles.consentLink}
                      onPointerDown={(event): void => event.stopPropagation()}
                      onClick={(event): void => event.stopPropagation()}
                    >
                      {t('terms of use')}
                    </a>
                  ),
                }}
              />
            }
          />

          <div className={styles.actions}>
            <Button
              variant="secondary"
              onPress={onLogout}
              isLoading={isLoggingOut}
              isDisabled={isConfirming}
              data-testid="consent-reconfirm-logout"
            >
              {t('Log out')}
            </Button>
            <Button
              variant="primary"
              onPress={onConfirm}
              isDisabled={!hasAgreed}
              isLoading={isConfirming}
              data-testid="consent-reconfirm-confirm"
            >
              {t('Confirm')}
            </Button>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  )
}
